<script setup>
import { computed, ref, watch } from "vue";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import Emoji from "@tiptap/extension-emoji";
import { useI18n } from "vue-i18n";
import {
    isAllowedMuralImage,
    MAX_MURAL_IMAGE_SIZE,
    MURAL_IMAGE_ACCEPT,
    readImageAsDataUrl
} from "@/utils/muralImage.js";

const props = defineProps({
    publishing: {
        type: Boolean,
        default: false
    },
    error: {
        type: String,
        default: ""
    }
});

const emit = defineEmits(["publish"]);
const { t } = useI18n();
const fileInput = ref(null);
const attachment = ref(null);
const attachmentName = ref("");
const attachmentError = ref("");
const hasEditorContent = ref(false);
const showEmojiMenu = ref(false);
const showLinkEditor = ref(false);
const linkUrl = ref("");

const fonts = computed(() => [
    { label: t("components.rhdigital.mural.composer.defaultFont"), value: "" },
    { label: "Arial", value: "Arial" },
    { label: "Georgia", value: "Georgia" },
    { label: "Verdana", value: "Verdana" },
    { label: t("components.rhdigital.mural.composer.monospace"), value: "monospace" }
]);

const emojiOptions = [
    { name: "grinning", symbol: "😀" },
    { name: "heart_eyes", symbol: "😍" },
    { name: "clap", symbol: "👏" },
    { name: "thumbs_up", symbol: "👍" },
    { name: "tada", symbol: "🎉" },
    { name: "rocket", symbol: "🚀" }
];

const editor = useEditor({
    content: "",
    extensions: [
        StarterKit.configure({ link: false, underline: false }),
        Underline,
        Link.configure({
            autolink: true,
            openOnClick: false,
            HTMLAttributes: {
                rel: "noopener noreferrer",
                target: "_blank"
            }
        }),
        TextStyle,
        FontFamily,
        Emoji.configure({ enableEmoticons: true })
    ],
    editorProps: {
        attributes: {
            class: "mural-composer__editor-content"
        }
    },
    onUpdate: ({ editor }) => {
        hasEditorContent.value = !editor.isEmpty;
    }
});

const canPublish = computed(() => {
    return !props.publishing && Boolean(attachment.value || hasEditorContent.value);
});

watch(() => props.publishing, value => {
    editor.value?.setEditable(!value);
});

function toggleLinkEditor() {
    linkUrl.value = editor.value?.getAttributes("link").href || "";
    showLinkEditor.value = !showLinkEditor.value;
    showEmojiMenu.value = false;
}

function applyLink() {
    const href = linkUrl.value.trim();
    if (!href) return;

    editor.value?.chain().focus().extendMarkRange("link").setLink({ href }).run();
    showLinkEditor.value = false;
}

function removeLink() {
    editor.value?.chain().focus().extendMarkRange("link").unsetLink().run();
    linkUrl.value = "";
    showLinkEditor.value = false;
}

function setFontFamily(event) {
    const fontFamily = event.target.value;
    const chain = editor.value?.chain().focus();

    if (fontFamily) {
        chain?.setFontFamily(fontFamily).run();
    } else {
        chain?.unsetFontFamily().run();
    }
}

function insertEmoji(emoji) {
    editor.value?.chain().focus().insertContent(emoji.symbol).run();
    showEmojiMenu.value = false;
}

function openFileDialog() {
    if (!props.publishing) fileInput.value?.click();
}

async function selectAttachment(event) {
    const file = event.target.files?.[0];
    attachmentError.value = "";

    if (!file) return;

    if (!isAllowedMuralImage(file)) {
        attachmentError.value = t("components.rhdigital.mural.composer.invalidImageType");
        event.target.value = "";
        return;
    }

    if (!file.size) {
        attachmentError.value = t("components.rhdigital.mural.composer.emptyFile");
        event.target.value = "";
        return;
    }

    if (file.size > MAX_MURAL_IMAGE_SIZE) {
        attachmentError.value = t("components.rhdigital.mural.composer.fileTooLarge", { size: 2 });
        event.target.value = "";
        return;
    }

    try {
        attachment.value = await readImageAsDataUrl(file);
        attachmentName.value = file.name;
    } catch {
        attachmentError.value = t("components.rhdigital.mural.composer.imageReadError");
        event.target.value = "";
    }
}

function removeAttachment() {
    attachment.value = null;
    attachmentName.value = "";
    attachmentError.value = "";
    if (fileInput.value) fileInput.value.value = "";
}

function publish() {
    if (!canPublish.value) return;

    emit("publish", {
        body: hasEditorContent.value ? editor.value?.getHTML() || "" : "",
        anexo: attachment.value
    });
}

function reset() {
    editor.value?.commands.clearContent();
    hasEditorContent.value = false;
    removeAttachment();
    showEmojiMenu.value = false;
    showLinkEditor.value = false;
    linkUrl.value = "";
}

defineExpose({ reset });
</script>

<template>
    <section class="mural-composer">
        <h2>{{ t("components.rhdigital.mural.composer.title") }}</h2>

        <div class="mural-composer__editor">
            <div v-if="editor" class="mural-composer__toolbar" :aria-label="t('components.rhdigital.mural.composer.tools')">
                <button type="button" :class="{ 'is-active': editor.isActive('bold') }" :title="t('components.rhdigital.mural.composer.bold')" @click="editor.chain().focus().toggleBold().run()">
                    <span class="material-symbols-outlined">format_bold</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('italic') }" :title="t('components.rhdigital.mural.composer.italic')" @click="editor.chain().focus().toggleItalic().run()">
                    <span class="material-symbols-outlined">format_italic</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('underline') }" :title="t('components.rhdigital.mural.composer.underline')" @click="editor.chain().focus().toggleUnderline().run()">
                    <span class="material-symbols-outlined">format_underlined</span>
                </button>
                <span class="mural-composer__separator"></span>
                <button type="button" :class="{ 'is-active': editor.isActive('bulletList') }" :title="t('components.rhdigital.mural.composer.bulletList')" @click="editor.chain().focus().toggleBulletList().run()">
                    <span class="material-symbols-outlined">format_list_bulleted</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('orderedList') }" :title="t('components.rhdigital.mural.composer.orderedList')" @click="editor.chain().focus().toggleOrderedList().run()">
                    <span class="material-symbols-outlined">format_list_numbered</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('link') }" :title="t('components.rhdigital.mural.composer.link')" @click="toggleLinkEditor">
                    <span class="material-symbols-outlined">link</span>
                </button>
                <select class="mural-composer__font" :aria-label="t('components.rhdigital.mural.composer.font')" :disabled="publishing" @change="setFontFamily">
                    <option v-for="font in fonts" :key="font.value" :value="font.value">{{ font.label }}</option>
                </select>
            </div>

            <div v-if="showLinkEditor" class="mural-composer__link-editor">
                <input v-model="linkUrl" type="url" :placeholder="t('components.rhdigital.mural.composer.linkPlaceholder')" @keyup.enter="applyLink">
                <button type="button" @click="applyLink">{{ t("components.rhdigital.mural.composer.applyLink") }}</button>
                <button type="button" @click="removeLink">{{ t("components.rhdigital.mural.composer.removeLink") }}</button>
            </div>

            <div class="mural-composer__editor-surface">
                <span v-if="!hasEditorContent" class="mural-composer__placeholder">
                    {{ t("components.rhdigital.mural.composer.placeholder") }}
                </span>
                <EditorContent :editor="editor" />
            </div>
        </div>

        <div v-if="attachment" class="mural-composer__attachment">
            <img :src="attachment" :alt="t('components.rhdigital.mural.composer.selectedImageAlt')">
            <div class="mural-composer__attachment-info">
                <span class="material-symbols-outlined">image</span>
                <span class="mural-composer__attachment-name">{{ attachmentName }}</span>
                <button type="button" :title="t('components.rhdigital.mural.composer.removeAttachment')" @click="removeAttachment">
                    <span class="material-symbols-outlined">close</span>
                </button>
            </div>
        </div>

        <p v-if="attachmentError" class="mural-composer__error">{{ attachmentError }}</p>
        <p v-if="error" class="mural-composer__error">{{ error }}</p>

        <div class="mural-composer__footer">
            <div class="mural-composer__tools">
                <div class="mural-composer__emoji-wrapper">
                    <button type="button" :title="t('components.rhdigital.mural.composer.emoji')" @click="showEmojiMenu = !showEmojiMenu; showLinkEditor = false">
                        <span class="material-symbols-outlined">mood</span>
                    </button>
                    <div v-if="showEmojiMenu" class="mural-composer__emoji-menu">
                        <button v-for="emoji in emojiOptions" :key="emoji.name" type="button" :title="t(`components.rhdigital.mural.composer.emojiNames.${emoji.name}`)" @click="insertEmoji(emoji)">
                            {{ emoji.symbol }}
                        </button>
                    </div>
                </div>

                <button type="button" :title="t('components.rhdigital.mural.composer.attachFile')" @click="openFileDialog">
                    <span class="material-symbols-outlined">image</span>
                </button>
                <input
                    ref="fileInput"
                    type="file"
                    :accept="MURAL_IMAGE_ACCEPT"
                    class="mural-composer__file-input"
                    @change="selectAttachment"
                >
            </div>

            <button class="mural-composer__publish" type="button" :disabled="!canPublish" @click="publish">
                <v-progress-circular v-if="publishing" indeterminate :size="17" :width="2" />
                <span v-else class="material-symbols-outlined">send</span>
                {{ publishing ? t("components.rhdigital.mural.composer.publishing") : t("components.rhdigital.mural.composer.publish") }}
            </button>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.mural-composer {
    padding: 22px 20px 18px;
    border: 1px solid #dde5f3;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 12px 30px rgba(34, 48, 73, 0.04);

    h2 {
        margin: 0 0 16px;
        color: #2f3b52;
        font-size: 20px;
        font-weight: 800;
    }

    &__editor {
        overflow: hidden;
        border: 1px solid #dce5f5;
        border-radius: 8px;
        background: #f8faff;

        &:focus-within {
            border-color: #625bf6;
            box-shadow: 0 0 0 3px rgba(98, 91, 246, 0.12);
        }
    }

    &__toolbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
        min-height: 42px;
        padding: 6px 8px;
        border-bottom: 1px solid #dce5f5;

        button {
            display: inline-grid;
            place-items: center;
            width: 30px;
            height: 30px;
            padding: 0;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: #667892;
            cursor: pointer;

            &:hover,
            &.is-active {
                background: #e8e7ff;
                color: #5149e8;
            }
        }

        .material-symbols-outlined {
            font-size: 19px;
        }
    }

    &__separator {
        width: 1px;
        height: 22px;
        margin: 0 3px;
        background: #dce5f5;
    }

    &__editor-surface {
        position: relative;
    }

    &__placeholder {
        position: absolute;
        top: 16px;
        left: 16px;
        color: #a6b2c6;
        pointer-events: none;
    }

    &__font {
        width: 126px;
        height: 30px;
        margin-left: auto;
        padding: 0 8px;
        border: 1px solid #d5deed;
        border-radius: 6px;
        outline: none;
        background: #fff;
        color: #4d5d73;
        font-size: 12px;
    }

    :deep(.mural-composer__editor-content) {
        min-height: 112px;
        padding: 16px;
        outline: none;
        color: #2f3b52;
        font-size: 15px;
        line-height: 1.55;

        p {
            margin: 0 0 8px;
        }

        p:last-child {
            margin-bottom: 0;
        }

        ul,
        ol {
            margin: 8px 0;
            padding-left: 24px;
        }

        a {
            color: #5149e8;
            text-decoration: underline;
        }
    }

    &__link-editor {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto auto;
        gap: 8px;
        padding: 8px;
        border-bottom: 1px solid #dce5f5;

        input {
            min-width: 0;
            height: 32px;
            padding: 0 10px;
            border: 1px solid #d5deed;
            border-radius: 6px;
            outline: none;
        }

        button {
            padding: 0 10px;
            border: 0;
            border-radius: 6px;
            background: #e8e7ff;
            color: #5149e8;
            font-size: 12px;
            font-weight: 700;
        }
    }

    &__attachment {
        overflow: hidden;
        margin-top: 12px;
        border: 1px solid #dce5f5;
        border-radius: 8px;
        background: #f8faff;
        color: #52657f;

        > img {
            display: block;
            width: 100%;
            max-height: 420px;
            object-fit: contain;
            background: #eef2f9;
        }

        &-info {
            display: flex;
            align-items: center;
            gap: 8px;
            min-height: 42px;
            padding: 7px 10px;
            border-top: 1px solid #dce5f5;
        }

        button {
            display: grid;
            place-items: center;
            width: 28px;
            height: 28px;
            margin-left: auto;
            padding: 0;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: #7c8da6;
        }
    }

    &__attachment-name {
        overflow: hidden;
        min-width: 0;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__error {
        margin: 10px 0 0;
        color: #dc2626;
        font-size: 13px;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-top: 16px;
    }

    &__tools {
        display: flex;
        align-items: center;
        gap: 8px;

        > button,
        > div > button {
            display: inline-grid;
            place-items: center;
            width: 32px;
            height: 32px;
            padding: 0;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: #7589a6;
            cursor: pointer;

            &:hover {
                background: #f0efff;
                color: #625bf6;
            }
        }
    }

    &__emoji-wrapper {
        position: relative;
    }

    &__emoji-menu {
        position: absolute;
        bottom: 40px;
        left: 0;
        z-index: 5;
        display: grid;
        grid-template-columns: repeat(3, 36px);
        gap: 4px;
        padding: 8px;
        border: 1px solid #dce5f5;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0 10px 24px rgba(34, 48, 73, 0.14);

        button {
            width: 36px;
            height: 36px;
            border: 0;
            border-radius: 6px;
            background: transparent;
            font-size: 20px;
            cursor: pointer;

            &:hover {
                background: #f0efff;
            }
        }
    }

    &__file-input {
        display: none;
    }

    &__publish {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-width: 116px;
        height: 36px;
        padding: 0 18px;
        border: 0;
        border-radius: 8px;
        background: #625bf6;
        color: #fff;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;

        &:disabled {
            background: #f0efff;
            color: #9995dd;
            cursor: not-allowed;
        }
    }
}

.dark-theme {
    .mural-composer {
        border-color: var(--borderColor);
        background: #0c1427;
        box-shadow: none;

        h2 {
            color: var(--whiteColor);
        }

        &__editor,
        &__attachment {
            border-color: var(--borderColor);
            background: #15203c;
        }

        &__toolbar,
        &__link-editor {
            border-color: var(--borderColor);
        }

        &__toolbar button,
        &__tools button {
            color: var(--bodyColor);

            &:hover,
            &.is-active {
                background: rgba(98, 91, 246, 0.2);
                color: #aaa6ff;
            }
        }

        &__font,
        &__link-editor input {
            border-color: var(--borderColor);
            background: #0c1427;
            color: var(--whiteColor);
        }

        :deep(.mural-composer__editor-content) {
            color: var(--whiteColor);
        }

        &__attachment {
            color: var(--bodyColor);

            > img {
                background: #0c1427;
            }

            &-info {
                border-color: var(--borderColor);
            }
        }

        &__emoji-menu {
            border-color: var(--borderColor);
            background: #15203c;
        }

        &__publish:disabled {
            background: rgba(98, 91, 246, 0.16);
            color: #8884cc;
        }
    }
}

@media (max-width: 575px) {
    .mural-composer {
        padding: 18px 16px;

        h2 {
            font-size: 18px;
        }

        &__font {
            width: 100%;
            margin: 4px 0 0;
        }

        &__link-editor {
            grid-template-columns: 1fr 1fr;

            input {
                grid-column: 1 / -1;
            }
        }

        &__footer {
            align-items: stretch;
            flex-direction: column;
        }

        &__publish {
            width: 100%;
        }
    }
}
</style>
